import { Environment, useGLTF } from "@react-three/drei";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { AnimatePresence, motion } from "framer-motion";
import type React from "react";
import { Component, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Group, Object3D } from "three";
import * as THREE from "three";
import bundledModelUrl from "../assets/3d-triffin-bot.glb?url";
import { type GlobalPointerState, useGlobalPointerRef } from "./use-global-pointer";

const FORCE_BOT_ERROR_BOUNDARY_TEST = false;

export type TTriffinBotFit = "avatar" | "full";

export interface TriffinBotProps {
	className?: string;
	/** Override the bundled GLB URL (e.g. rebrand public `/models/…`). */
	modelUrl?: string;
	restartIntroToken?: number;
	shouldCycleSleepWake?: boolean;
	onLoaded?: () => void;
	/**
	 * `avatar` (default) — tight head crop for chat chrome.
	 * `full` — pull camera back so antenna + body clear the canvas (landing).
	 */
	fit?: TTriffinBotFit;
}

const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

const CONFIG = {
	ANIMATION_DURATION: 1.0,
	ROTATION_LERP_SPEED: 16,

	/** Headshot scale — fills most of the tile with a little padding. */
	SCALE: 0.26,
	/** Slightly smaller so the full figure (antenna → body) fits the landing box. */
	SCALE_FULL: 0.24,

	/** Face-centered Y for a square avatar crop (camera has no lookAt). */
	BOT_POSITION: [0, 0.16, 1.4] as const,
	/** Lower in frame so the antenna isn't clipped at the top. */
	BOT_POSITION_FULL: [0, 0.12, 1.4] as const,
	BOT_ROTATION_Y: -Math.PI / 2,

	// Camera — avatar face height (unchanged; size via SCALE).
	CAMERA_POSITION: [0, 0.76, 3.95] as const,
	CAMERA_POSITION_FULL: [0, 0.75, 4.4] as const,
	CAMERA_FOV: 45,
	MOUSE_DEPTH: 0.85, // Sensitivity

	ANTENNA: {
		LERP_SPEED: 1,
		SPIN_LERP_SPEED: 2,
		FAST_MOVEMENT_THRESHOLD: 0.2,
		SPIN_DURATION: 0.5,
		HOVER_SPIN_SPEED: 7.5,
	},

	EYES: {
		HOVER_RELAXED_Y_SCALE: 0.1,
		HOVER_LERP_SPEED: 10,
		SLEEP_CLOSED_Y_SCALE: 0.1,
		SLEEP_AWAKE_HOLD_DURATION: 30,
		SLEEP_CLOSE_DURATION: 1.2,
		SLEEP_CLOSED_HOLD_DURATION: 30,
		SLEEP_WAKE_DURATION: 1.2,
	},

	SLEEP_POSE: {
		PITCH_X: -0.35,
		YAW_Y: -0.3,
		ROCK_SPEED_HZ: 0.16,
		ROCK_Z_AMPLITUDE: 0.26,
		ROCK_BOUNCE_FACTOR: 0.35,
		POSITION_Y: 0.1,
		POSITION_LERP_SPEED: 4,
	},

	AMBIENT_LIGHT_INTENSITY: 0.5,

	BLINK_INTERVAL: 5,
	BLINK_DURATION: 0.3,
	BLINK_SCALE_MIN: 0.0,

	// Idle rock (when cursor isn't available / not over the canvas)
	SWAY: {
		SPEED_HZ: 0.18,
		AMPLITUDE_Z: 0.22,
		BOUNCE_FACTOR: 0.3,
	},

	WINK_DELAY: 0.08,
	WINK_DURATION: 0.5,
	WINK_HOLD_DURATION: 0.3,
	WINK_SCALE_MIN: 0.0,
	WINK_EYE_INDEX: 0,
} as const;

type IntroState = {
	startTime: number | null;
	progress: number;
	complete: boolean;
};

type SleepState = {
	isSleeping: boolean;
	sleepBlend: number;
};

type ContainerMetrics = {
	centerX: number;
	centerY: number;
	halfViewportWidth: number;
	halfViewportHeight: number;
	ready: boolean;
};

function useContainerMetrics(
	containerRef: React.RefObject<HTMLDivElement | null>,
	containerMetricsRef: React.MutableRefObject<ContainerMetrics>,
) {
	useEffect(() => {
		if (typeof window === "undefined") return;

		const updateContainerMetrics = () => {
			const container = containerRef.current;
			if (!container) return;

			const rect = container.getBoundingClientRect();
			containerMetricsRef.current = {
				centerX: rect.left + rect.width / 2,
				centerY: rect.top + rect.height / 2,
				halfViewportWidth: Math.max(window.innerWidth, 1) / 2,
				halfViewportHeight: Math.max(window.innerHeight, 1) / 2,
				ready: true,
			};
		};

		updateContainerMetrics();

		const resizeObserver = new ResizeObserver(updateContainerMetrics);
		if (containerRef.current) resizeObserver.observe(containerRef.current);
		window.addEventListener("resize", updateContainerMetrics, { passive: true });
		window.addEventListener("scroll", updateContainerMetrics, { passive: true });

		return () => {
			resizeObserver.disconnect();
			window.removeEventListener("resize", updateContainerMetrics);
			window.removeEventListener("scroll", updateContainerMetrics);
		};
	}, [containerRef, containerMetricsRef]);
}

function useRelativePointer(
	pointerRef: React.MutableRefObject<GlobalPointerState>,
	containerMetricsRef: React.MutableRefObject<ContainerMetrics>,
	relativePointerRef: React.MutableRefObject<GlobalPointerState>,
) {
	useFrame(() => {
		const pointer = pointerRef.current;
		if (!pointer.isActive) {
			relativePointerRef.current.isActive = false;
			return;
		}

		const metrics = containerMetricsRef.current;
		if (!metrics.ready) {
			relativePointerRef.current.x = pointer.x;
			relativePointerRef.current.y = pointer.y;
			relativePointerRef.current.clientX = pointer.clientX;
			relativePointerRef.current.clientY = pointer.clientY;
			relativePointerRef.current.isActive = true;
			return;
		}

		relativePointerRef.current.x = THREE.MathUtils.clamp(
			(pointer.clientX - metrics.centerX) / metrics.halfViewportWidth,
			-1,
			1,
		);
		relativePointerRef.current.y = THREE.MathUtils.clamp(
			-(pointer.clientY - metrics.centerY) / metrics.halfViewportHeight,
			-1,
			1,
		);
		relativePointerRef.current.clientX = pointer.clientX;
		relativePointerRef.current.clientY = pointer.clientY;
		relativePointerRef.current.isActive = true;
	});
}

function useLiveContainerMetrics(
	containerRef: React.RefObject<HTMLDivElement | null>,
	containerMetricsRef: React.MutableRefObject<ContainerMetrics>,
	pointerRef: React.MutableRefObject<GlobalPointerState>,
) {
	useFrame(() => {
		if (!pointerRef.current.isActive) return;
		const container = containerRef.current;
		if (!container) return;

		const rect = container.getBoundingClientRect();
		containerMetricsRef.current.centerX = rect.left + rect.width / 2;
		containerMetricsRef.current.centerY = rect.top + rect.height / 2;
		containerMetricsRef.current.halfViewportWidth = Math.max(window.innerWidth, 1) / 2;
		containerMetricsRef.current.halfViewportHeight = Math.max(window.innerHeight, 1) / 2;
		containerMetricsRef.current.ready = true;
	});
}

function useBotHoverState(
	containerRef: React.RefObject<HTMLDivElement | null>,
	pointerRef: React.MutableRefObject<GlobalPointerState>,
	isHoveredRef: React.MutableRefObject<boolean>,
) {
	useFrame(() => {
		const pointer = pointerRef.current;
		const container = containerRef.current;
		if (!pointer.isActive || !container) {
			isHoveredRef.current = false;
			return;
		}

		const rect = container.getBoundingClientRect();
		isHoveredRef.current =
			pointer.clientX >= rect.left &&
			pointer.clientX <= rect.right &&
			pointer.clientY >= rect.top &&
			pointer.clientY <= rect.bottom;
	});
}

function useBotParts(scene: Object3D) {
	return useMemo(() => {
		let antenna: Object3D | null = null;
		const eyes: Object3D[] = [];
		const eyeOriginalScales: THREE.Vector3[] = [];

		scene.traverse((child) => {
			if (child.name === "Antenna" || child.name.includes("Antenna")) {
				antenna = child;
			}
			if (child.name.includes("Eye")) {
				eyes.push(child);
				eyeOriginalScales.push(child.scale.clone());
			}
		});

		return { antenna, eyes, eyeOriginalScales };
	}, [scene]);
}

function useIntroSpinAndZoom(
	groupRef: React.RefObject<Group>,
	introRef: React.MutableRefObject<IntroState>,
	introEnabledRef: React.MutableRefObject<boolean>,
	targetScale: number,
) {
	useFrame(({ clock }) => {
		const group = groupRef.current;
		if (!group) return;

		// Hold the bot hidden (and the intro paused at t=0) until the GPU context is warmed.
		// Starting the spin only once shaders are compiled means it plays smoothly instead of
		// stuttering while the first frame's shader/texture work lands on the main thread.
		if (!introEnabledRef.current) {
			group.scale.setScalar(0);
			introRef.current.startTime = null;
			introRef.current.progress = 0;
			introRef.current.complete = false;
			return;
		}

		if (introRef.current.startTime === null) {
			introRef.current.startTime = clock.elapsedTime;
		}

		const elapsed = clock.elapsedTime - introRef.current.startTime;
		const progress = Math.min(elapsed / CONFIG.ANIMATION_DURATION, 1);
		introRef.current.progress = progress;
		introRef.current.complete = progress >= 1;

		if (progress < 1) {
			const easedProgress = easeOut(progress);
			group.rotation.y = 2 * Math.PI * easedProgress;
			group.scale.setScalar(targetScale * easedProgress);
			return;
		}

		// Lock final scale once intro completes (avoid drift from other mutations)
		group.scale.setScalar(targetScale);
	});
}

function useWinkAfterIntro(
	eyes: Object3D[],
	eyeOriginalScales: THREE.Vector3[],
	introRef: React.MutableRefObject<IntroState>,
) {
	const winkStartTimeRef = useRef<number | null>(null);
	const winkCompleteRef = useRef(false);

	useFrame(({ clock }) => {
		if (!introRef.current.complete) {
			const eye = eyes[CONFIG.WINK_EYE_INDEX];
			const originalScale = eyeOriginalScales[CONFIG.WINK_EYE_INDEX];
			if (eye && originalScale) {
				eye.scale.copy(originalScale);
			}
			winkStartTimeRef.current = null;
			winkCompleteRef.current = false;
			return;
		}
		if (winkCompleteRef.current) return;

		const eye = eyes[CONFIG.WINK_EYE_INDEX];
		const originalScale = eyeOriginalScales[CONFIG.WINK_EYE_INDEX];

		// If we can't locate the eye, just mark wink as complete to avoid repeated work.
		if (!eye || !originalScale) {
			winkCompleteRef.current = true;
			return;
		}

		if (winkStartTimeRef.current === null) {
			winkStartTimeRef.current = clock.elapsedTime + CONFIG.WINK_DELAY;
		}

		const winkStartTime = winkStartTimeRef.current;
		if (winkStartTime === null || clock.elapsedTime < winkStartTime) return;

		const winkElapsed = clock.elapsedTime - winkStartTime;
		const totalDuration = Math.max(CONFIG.WINK_DURATION, 0);
		const holdDuration = Math.min(Math.max(CONFIG.WINK_HOLD_DURATION, 0), totalDuration);
		const remaining = Math.max(totalDuration - holdDuration, 0);
		const closeDuration = remaining / 2;
		const openDuration = remaining / 2;

		const closeEnd = closeDuration;
		const holdEnd = closeDuration + holdDuration;
		const openEnd = closeDuration + holdDuration + openDuration;

		if (winkElapsed < closeEnd) {
			// Close: 1 -> min
			const t = closeDuration > 0 ? winkElapsed / closeDuration : 1;
			const winkScale = 1 - (1 - CONFIG.WINK_SCALE_MIN) * t;
			eye.scale.set(originalScale.x, originalScale.y * winkScale, originalScale.z);
			return;
		}

		if (winkElapsed < holdEnd) {
			// Hold closed at min
			eye.scale.set(originalScale.x, originalScale.y * CONFIG.WINK_SCALE_MIN, originalScale.z);
			return;
		}

		if (winkElapsed < openEnd) {
			// Open: min -> 1
			const t = openDuration > 0 ? (winkElapsed - holdEnd) / openDuration : 1;
			const winkScale = CONFIG.WINK_SCALE_MIN + (1 - CONFIG.WINK_SCALE_MIN) * t;
			eye.scale.set(originalScale.x, originalScale.y * winkScale, originalScale.z);
			return;
		}

		eye.scale.copy(originalScale);
		winkCompleteRef.current = true;
	});
}

function useLookAtPointerRotation(
	groupRef: React.RefObject<Group>,
	introRef: React.MutableRefObject<IntroState>,
	relativePointerRef: React.MutableRefObject<GlobalPointerState>,
	sleepStateRef: React.MutableRefObject<SleepState>,
) {
	const { camera } = useThree();
	const vec = useRef(new THREE.Vector3());
	const targetRotation = useRef(new THREE.Euler());
	const tempObject = useRef(new THREE.Object3D());
	const neutralRotation = useRef(new THREE.Euler(0, 0, 0));

	useFrame(({ clock }, delta) => {
		const group = groupRef.current;
		if (!group) return;
		if (!introRef.current.complete) return;

		const pointer = relativePointerRef.current;
		const sleepState = sleepStateRef.current;

		if (sleepState.isSleeping) {
			const omega = 2 * Math.PI * CONFIG.SLEEP_POSE.ROCK_SPEED_HZ;
			const t = clock.elapsedTime;
			const base = Math.sin(t * omega);
			const harmonic = Math.sin(t * omega * 2 + Math.PI / 4);
			const rockShape =
				(base + harmonic * CONFIG.SLEEP_POSE.ROCK_BOUNCE_FACTOR) / (1 + CONFIG.SLEEP_POSE.ROCK_BOUNCE_FACTOR);
			const rockZ = rockShape * CONFIG.SLEEP_POSE.ROCK_Z_AMPLITUDE;
			targetRotation.current.set(
				CONFIG.SLEEP_POSE.PITCH_X * sleepState.sleepBlend,
				CONFIG.SLEEP_POSE.YAW_Y * sleepState.sleepBlend,
				rockZ * sleepState.sleepBlend,
			);
		} else if (pointer.isActive) {
			vec.current.set(pointer.x, pointer.y, CONFIG.MOUSE_DEPTH);
			vec.current.unproject(camera);

			tempObject.current.position.copy(group.position);
			tempObject.current.lookAt(vec.current);
			targetRotation.current.copy(tempObject.current.rotation);
		} else {
			// Idle rock only on Z-axis.
			const t = clock.elapsedTime;
			const omega = 2 * Math.PI * CONFIG.SWAY.SPEED_HZ;
			const base = Math.sin(t * omega);
			const harmonic = Math.sin(t * omega * 2 + Math.PI / 4);
			const rockShape = (base + harmonic * CONFIG.SWAY.BOUNCE_FACTOR) / (1 + CONFIG.SWAY.BOUNCE_FACTOR);
			const rockZ = rockShape * CONFIG.SWAY.AMPLITUDE_Z;
			targetRotation.current.set(
				neutralRotation.current.x,
				neutralRotation.current.y,
				neutralRotation.current.z + rockZ,
			);
		}

		const lerpFactor = Math.min(CONFIG.ROTATION_LERP_SPEED * delta, 1);

		let yDiff = targetRotation.current.y - group.rotation.y;
		if (yDiff > Math.PI) yDiff -= 2 * Math.PI;
		if (yDiff < -Math.PI) yDiff += 2 * Math.PI;
		group.rotation.y += yDiff * lerpFactor;

		let xDiff = targetRotation.current.x - group.rotation.x;
		if (xDiff > Math.PI) xDiff -= 2 * Math.PI;
		if (xDiff < -Math.PI) xDiff += 2 * Math.PI;
		group.rotation.x += xDiff * lerpFactor;

		let zDiff = targetRotation.current.z - group.rotation.z;
		if (zDiff > Math.PI) zDiff -= 2 * Math.PI;
		if (zDiff < -Math.PI) zDiff += 2 * Math.PI;
		group.rotation.z += zDiff * lerpFactor;
	});
}

function useSleepVerticalOffset(
	groupRef: React.RefObject<Group>,
	introRef: React.MutableRefObject<IntroState>,
	sleepStateRef: React.MutableRefObject<SleepState>,
	awakeY: number,
) {
	useFrame((_, delta) => {
		const group = groupRef.current;
		if (!group) return;
		if (!introRef.current.complete) return;

		const sleepY = CONFIG.SLEEP_POSE.POSITION_Y;
		const targetY = THREE.MathUtils.lerp(awakeY, sleepY, sleepStateRef.current.sleepBlend);
		const lerpFactor = Math.min(CONFIG.SLEEP_POSE.POSITION_LERP_SPEED * delta, 1);

		group.position.y = THREE.MathUtils.lerp(group.position.y, targetY, lerpFactor);
	});
}

function useAntennaFollowAndSpin(
	antenna: Object3D | null,
	introRef: React.MutableRefObject<IntroState>,
	relativePointerRef: React.MutableRefObject<GlobalPointerState>,
	isHoveredRef: React.MutableRefObject<boolean>,
	sleepStateRef: React.MutableRefObject<SleepState>,
) {
	const previousMousePosition = useRef(new THREE.Vector2(0, 0));
	const mouseDelta = useRef(new THREE.Vector2(0, 0));
	const antennaSpinRotation = useRef(0);
	const wasPointerActiveRef = useRef(false);

	useFrame((_, delta) => {
		if (!antenna) return;
		if (!introRef.current.complete) return;
		if (sleepStateRef.current.isSleeping) return;

		if (isHoveredRef.current) {
			// While hovered, keep the antenna spinning continuously.
			antenna.rotation.y += CONFIG.ANTENNA.HOVER_SPIN_SPEED * delta;
			antenna.rotation.x = 0;
			antenna.rotation.z = 0;
			return;
		}

		const pointer = relativePointerRef.current;
		if (!pointer.isActive) {
			wasPointerActiveRef.current = false;
			return;
		}
		if (!wasPointerActiveRef.current) {
			previousMousePosition.current.set(pointer.x, pointer.y);
			wasPointerActiveRef.current = true;
		}

		mouseDelta.current.set(pointer.x - previousMousePosition.current.x, pointer.y - previousMousePosition.current.y);

		const horizontalSpeed = Math.abs(mouseDelta.current.x);
		if (horizontalSpeed > CONFIG.ANTENNA.FAST_MOVEMENT_THRESHOLD) {
			const spinDirection = mouseDelta.current.x > 0 ? 1 : -1;
			antennaSpinRotation.current += 6 * Math.PI * spinDirection;
		}

		const spinLerpFactor = Math.min(CONFIG.ANTENNA.SPIN_LERP_SPEED * delta, 1);
		antennaSpinRotation.current *= 1 - spinLerpFactor;

		const baseYRotation = pointer.x < 0 ? Math.PI : 0;
		const targetYRotation = baseYRotation + antennaSpinRotation.current;

		const antennaLerpFactor = Math.min(CONFIG.ANTENNA.LERP_SPEED * delta, 1);
		let antennaYDiff = targetYRotation - antenna.rotation.y;
		if (antennaYDiff > Math.PI) antennaYDiff -= 2 * Math.PI;
		if (antennaYDiff < -Math.PI) antennaYDiff += 2 * Math.PI;
		antenna.rotation.y += antennaYDiff * antennaLerpFactor;

		antenna.rotation.x = 0;
		antenna.rotation.z = 0;

		previousMousePosition.current.set(pointer.x, pointer.y);
	});
}

function useHoverRelaxedEyes(
	eyes: Object3D[],
	eyeOriginalScales: THREE.Vector3[],
	introRef: React.MutableRefObject<IntroState>,
	isHoveredRef: React.MutableRefObject<boolean>,
) {
	useFrame((_, delta) => {
		if (!introRef.current.complete) return;
		if (eyes.length === 0) return;

		const targetYScaleMultiplier = isHoveredRef.current ? CONFIG.EYES.HOVER_RELAXED_Y_SCALE : 1;
		const lerpFactor = Math.min(CONFIG.EYES.HOVER_LERP_SPEED * delta, 1);

		eyes.forEach((eye, index) => {
			const originalScale = eyeOriginalScales[index];
			if (!originalScale) return;

			const targetY = originalScale.y * targetYScaleMultiplier;
			eye.scale.y = THREE.MathUtils.lerp(eye.scale.y, targetY, lerpFactor);
		});
	});
}

type SleepCyclePhase = "awake" | "closing" | "sleeping" | "waking";

function useSleepWakeCycle(
	eyes: Object3D[],
	eyeOriginalScales: THREE.Vector3[],
	introRef: React.MutableRefObject<IntroState>,
	shouldCycleSleepWake: boolean,
	sleepStateRef: React.MutableRefObject<SleepState>,
	onSleepStateChange?: (isSleeping: boolean) => void,
) {
	const phaseRef = useRef<SleepCyclePhase>("awake");
	const phaseStartTimeRef = useRef<number | null>(null);
	const wasCyclingRef = useRef(false);
	const lastReportedSleepingRef = useRef<boolean | null>(null);

	const setPhase = (phase: SleepCyclePhase, now: number) => {
		phaseRef.current = phase;
		phaseStartTimeRef.current = now;
	};

	useFrame(({ clock }) => {
		if (!introRef.current.complete) return;
		if (eyes.length === 0) return;

		if (!shouldCycleSleepWake) {
			phaseRef.current = "awake";
			phaseStartTimeRef.current = null;
			sleepStateRef.current.isSleeping = false;
			sleepStateRef.current.sleepBlend = 0;
			if (lastReportedSleepingRef.current !== false) {
				onSleepStateChange?.(false);
				lastReportedSleepingRef.current = false;
			}

			if (wasCyclingRef.current) {
				eyes.forEach((eye, index) => {
					const originalScale = eyeOriginalScales[index];
					if (!originalScale) return;
					eye.scale.copy(originalScale);
				});
			}

			wasCyclingRef.current = false;
			return;
		}

		wasCyclingRef.current = true;

		const now = clock.elapsedTime;
		if (phaseStartTimeRef.current === null) {
			phaseStartTimeRef.current = now;
		}

		const phaseElapsed = now - phaseStartTimeRef.current;
		let eyeScaleYMultiplier = 1;
		let sleepBlend = 0;

		switch (phaseRef.current) {
			case "awake":
				eyeScaleYMultiplier = 1;
				sleepBlend = 0;
				if (phaseElapsed >= CONFIG.EYES.SLEEP_AWAKE_HOLD_DURATION) {
					setPhase("closing", now);
				}
				break;
			case "closing": {
				const closeProgress = Math.min(phaseElapsed / CONFIG.EYES.SLEEP_CLOSE_DURATION, 1);
				eyeScaleYMultiplier = THREE.MathUtils.lerp(1, CONFIG.EYES.SLEEP_CLOSED_Y_SCALE, closeProgress);
				sleepBlend = closeProgress;
				if (closeProgress >= 1) {
					setPhase("sleeping", now);
				}
				break;
			}
			case "sleeping":
				eyeScaleYMultiplier = CONFIG.EYES.SLEEP_CLOSED_Y_SCALE;
				sleepBlend = 1;
				if (phaseElapsed >= CONFIG.EYES.SLEEP_CLOSED_HOLD_DURATION) {
					setPhase("waking", now);
				}
				break;
			case "waking": {
				const wakeProgress = Math.min(phaseElapsed / CONFIG.EYES.SLEEP_WAKE_DURATION, 1);
				eyeScaleYMultiplier = THREE.MathUtils.lerp(CONFIG.EYES.SLEEP_CLOSED_Y_SCALE, 1, wakeProgress);
				sleepBlend = 1 - wakeProgress;
				if (wakeProgress >= 1) {
					setPhase("awake", now);
				}
				break;
			}
		}

		sleepStateRef.current.sleepBlend = THREE.MathUtils.clamp(sleepBlend, 0, 1);
		sleepStateRef.current.isSleeping = sleepStateRef.current.sleepBlend > 0.001;
		if (lastReportedSleepingRef.current !== sleepStateRef.current.isSleeping) {
			onSleepStateChange?.(sleepStateRef.current.isSleeping);
			lastReportedSleepingRef.current = sleepStateRef.current.isSleeping;
		}

		eyes.forEach((eye, index) => {
			const originalScale = eyeOriginalScales[index];
			if (!originalScale) return;
			eye.scale.set(originalScale.x, originalScale.y * eyeScaleYMultiplier, originalScale.z);
		});
	});
}

type BotProps = {
	pointerRef: React.MutableRefObject<GlobalPointerState>;
	containerMetricsRef: React.MutableRefObject<ContainerMetrics>;
	containerRef: React.RefObject<HTMLDivElement | null>;
	modelUrl: string;
	introEnabled: boolean;
	restartIntroToken?: number;
	shouldCycleSleepWake?: boolean;
	onSleepStateChange?: (isSleeping: boolean) => void;
	fit: TTriffinBotFit;
};

/**
 * Warms the WebGL context once the model and environment have resolved: parallel shader
 * compilation (three's `compileAsync`) plus a first GPU upload happen here, off the visible
 * intro. When done it reports ready so the bot can be revealed and its intro started without
 * the cold-start hitch. Rendered inside the same Suspense boundary as the model/environment,
 * so it only mounts after both are loaded and the scene graph is complete.
 */
function WarmUp({ onReady }: { onReady: () => void }) {
	const gl = useThree((state) => state.gl);
	const scene = useThree((state) => state.scene);
	const camera = useThree((state) => state.camera);

	useEffect(() => {
		let cancelled = false;

		const finish = () => {
			if (cancelled) return;
			onReady();
		};

		const warm = async () => {
			try {
				const renderer = gl as THREE.WebGLRenderer & {
					compileAsync?: (scene: THREE.Object3D, camera: THREE.Camera) => Promise<unknown>;
				};
				if (typeof renderer.compileAsync === "function") {
					await renderer.compileAsync(scene, camera);
				} else {
					gl.compile(scene, camera);
				}
			} catch {
				// Warming is best-effort; reveal the bot regardless so it never gets stuck hidden.
			}
			// One frame after compile lets the first real GPU upload settle before we reveal.
			requestAnimationFrame(finish);
		};

		void warm();

		return () => {
			cancelled = true;
		};
	}, [gl, scene, camera, onReady]);

	return null;
}

type BotErrorBoundaryProps = {
	children: React.ReactNode;
};

type BotErrorBoundaryState = {
	hasError: boolean;
};

class BotErrorBoundary extends Component<BotErrorBoundaryProps, BotErrorBoundaryState> {
	constructor(props: BotErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(): BotErrorBoundaryState {
		return { hasError: true };
	}

	override componentDidCatch() {
		// Keep UI stable with a lightweight animated fallback.
	}

	override render() {
		if (this.state.hasError) {
			// Keep the avatar/full slot square — empty fill, no canvas blowout.
			return <div className="absolute inset-0 h-full w-full max-h-full max-w-full" aria-hidden="true" />;
		}

		return this.props.children;
	}
}

function Bot({
	pointerRef,
	containerMetricsRef,
	containerRef,
	modelUrl,
	introEnabled,
	restartIntroToken,
	shouldCycleSleepWake = false,
	onSleepStateChange,
	fit,
}: BotProps) {
	const groupRef = useRef<Group>(null);
	const gltf = useGLTF(modelUrl);
	// `useGLTF` caches models globally, so each bot instance must use a cloned scene.
	// Otherwise mounting a second <primitive> will re-parent the same object and shared
	// transform mutations (eyes/antenna) can leak between instances and remounts.
	const scene = useMemo(() => gltf.scene.clone(true), [gltf.scene]);
	const { antenna, eyes, eyeOriginalScales } = useBotParts(scene);
	const relativePointerRef = useRef<GlobalPointerState>({
		x: 0,
		y: 0,
		clientX: 0,
		clientY: 0,
		isActive: false,
	});

	const introRef = useRef<IntroState>({
		startTime: null,
		progress: 0,
		complete: false,
	});
	const sleepStateRef = useRef<SleepState>({
		isSleeping: false,
		sleepBlend: 0,
	});
	const isHoveredRef = useRef(false);
	const introEnabledRef = useRef(introEnabled);
	const isFullFit = fit === "full";
	const targetScale = isFullFit ? CONFIG.SCALE_FULL : CONFIG.SCALE;
	const botPosition = isFullFit ? CONFIG.BOT_POSITION_FULL : CONFIG.BOT_POSITION;
	const awakeY = botPosition[1];

	if (FORCE_BOT_ERROR_BOUNDARY_TEST) {
		throw new Error("Intentional bot error boundary test");
	}

	useEffect(() => {
		introEnabledRef.current = introEnabled;
	}, [introEnabled]);

	useEffect(() => {
		introRef.current.startTime = null;
		introRef.current.progress = 0;
		introRef.current.complete = false;
	}, [restartIntroToken]);

	// Order matters: intro should run before the behaviors that depend on "complete".
	useLiveContainerMetrics(containerRef, containerMetricsRef, pointerRef);
	useBotHoverState(containerRef, pointerRef, isHoveredRef);
	useRelativePointer(pointerRef, containerMetricsRef, relativePointerRef);
	useIntroSpinAndZoom(groupRef, introRef, introEnabledRef, targetScale);
	useWinkAfterIntro(eyes, eyeOriginalScales, introRef);
	useLookAtPointerRotation(groupRef, introRef, relativePointerRef, sleepStateRef);
	useSleepVerticalOffset(groupRef, introRef, sleepStateRef, awakeY);
	useAntennaFollowAndSpin(antenna, introRef, relativePointerRef, isHoveredRef, sleepStateRef);
	useHoverRelaxedEyes(eyes, eyeOriginalScales, introRef, isHoveredRef);
	useSleepWakeCycle(eyes, eyeOriginalScales, introRef, shouldCycleSleepWake, sleepStateRef, onSleepStateChange);

	return (
		<group ref={groupRef} position={botPosition} scale={0}>
			<primitive object={scene} rotation-y={CONFIG.BOT_ROTATION_Y} />
		</group>
	);
}

export function TriffinBot({
	className,
	modelUrl = bundledModelUrl,
	restartIntroToken,
	shouldCycleSleepWake = false,
	onLoaded,
	fit = "avatar",
}: TriffinBotProps) {
	const pointerRef = useGlobalPointerRef();
	const [isSleeping, setIsSleeping] = useState(false);
	const [isReady, setIsReady] = useState(false);
	const [showPlaceholder, setShowPlaceholder] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const containerMetricsRef = useRef<ContainerMetrics>({
		centerX: 0,
		centerY: 0,
		halfViewportWidth: 1,
		halfViewportHeight: 1,
		ready: false,
	});
	const cameraPosition = fit === "full" ? CONFIG.CAMERA_POSITION_FULL : CONFIG.CAMERA_POSITION;

	useEffect(() => {
		useGLTF.preload(modelUrl);
	}, [modelUrl]);

	useContainerMetrics(containerRef, containerMetricsRef);

	const hasReportedReadyRef = useRef(false);
	const markReady = useCallback(() => {
		setIsReady(true);
		if (!hasReportedReadyRef.current) {
			hasReportedReadyRef.current = true;
			onLoaded?.();
		}
	}, [onLoaded]);

	// Safety net: if warming never completes (unsupported GPU path, asset stall), still reveal the
	// bot and report ready so consumers gating on `onLoaded` (e.g. the chat widget) never get stuck.
	useEffect(() => {
		if (isReady) return;
		const timeout = window.setTimeout(markReady, 8000);
		return () => window.clearTimeout(timeout);
	}, [isReady, markReady]);

	// Only fall back to the 2D placeholder if warming is genuinely slow (e.g. the first bot in a
	// session, before the HDRI is cached). The common path warms in a few frames, so the placeholder
	// never renders and there is no flash — just the bot spinning in.
	useEffect(() => {
		if (isReady) return;
		const timeout = window.setTimeout(() => setShowPlaceholder(true), 500);
		return () => window.clearTimeout(timeout);
	}, [isReady]);

	const rootClassName = ["relative h-full w-full max-h-full max-w-full overflow-hidden", className]
		.filter(Boolean)
		.join(" ");

	return (
		<div ref={containerRef} className={rootClassName}>
			<BotErrorBoundary>
				{/*
				  Pin the WebGL tree to the parent box. Without absolute inset-0, R3F's canvas can
				  keep the HTML default height (150) and blow past a 48×48 avatar — cropping to only
				  the antenna tip.
				*/}
				<div className="absolute inset-0 h-full w-full max-h-full max-w-full overflow-hidden">
					<motion.div
						className="absolute inset-0 h-full w-full max-h-full max-w-full overflow-hidden"
						initial={{ opacity: 0 }}
						animate={{ opacity: isReady ? 1 : 0 }}
						transition={{ duration: 0.2, ease: "easeOut" }}
					>
						<Canvas
							className="absolute inset-0 !h-full !w-full max-h-full max-w-full"
							style={{
								display: "block",
								width: "100%",
								height: "100%",
								maxWidth: "100%",
								maxHeight: "100%",
								background: "transparent",
								pointerEvents: "none",
							}}
							resize={{ offsetSize: true }}
							camera={{ position: cameraPosition, fov: CONFIG.CAMERA_FOV }}
							gl={{ alpha: true, antialias: true }}
							onCreated={({ gl }) => {
								gl.setClearColor(0x000000, 0);
							}}
						>
							<ambientLight intensity={CONFIG.AMBIENT_LIGHT_INTENSITY} color="#ffffff" />
							<Suspense fallback={null}>
								<Environment preset="dawn" environmentIntensity={1} environmentRotation={new THREE.Euler(0.6, 1, 0)} />
								<Bot
									pointerRef={pointerRef}
									containerMetricsRef={containerMetricsRef}
									containerRef={containerRef}
									modelUrl={modelUrl}
									introEnabled={isReady}
									restartIntroToken={restartIntroToken}
									shouldCycleSleepWake={shouldCycleSleepWake}
									onSleepStateChange={setIsSleeping}
									fit={fit}
								/>
								<WarmUp onReady={markReady} />
							</Suspense>
						</Canvas>
					</motion.div>
					<AnimatePresence>
						{!isReady && showPlaceholder ? (
							<motion.div
								key="bot-warmup-placeholder"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.2, ease: "easeOut" }}
								className="pointer-events-none absolute inset-0"
								aria-hidden="true"
							/>
						) : null}
					</AnimatePresence>
					<AnimatePresence>
						{isSleeping ? (
							<motion.div
								key="sleep-dream-icons"
								initial={{ opacity: 0, y: 6 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -6 }}
								transition={{ duration: 0.24, ease: "easeOut" }}
								className="pointer-events-none absolute top-3 right-3 z-30"
							>
								<motion.div
									className="absolute right-1 top-4"
									animate={{ y: [0, -6, 0], opacity: [0.7, 1, 0.7], rotate: [0, 4, 0] }}
									transition={{ duration: 2.2, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }}
								>
									<span className="text-foreground/70 text-base font-semibold">Z</span>
								</motion.div>
								<motion.div
									className="absolute top-5 right-5"
									animate={{ y: [0, -5, 0], opacity: [0.55, 0.85, 0.55], rotate: [0, -6, 0] }}
									transition={{
										duration: 2.4,
										repeat: Number.POSITIVE_INFINITY,
										ease: "easeInOut",
										delay: 0.35,
									}}
								>
									<span className="text-foreground/55 text-sm font-semibold">z</span>
								</motion.div>
							</motion.div>
						) : null}
					</AnimatePresence>
				</div>
			</BotErrorBoundary>
		</div>
	);
}

useGLTF.preload(bundledModelUrl);
